import SearchAPI from '../../api/search';
import types from '../mutation-types';
import camelcaseKeys from 'camelcase-keys';
import {
  webOrdersAPI,
  webCustomersAPI,
  webProductsAPI,
} from '../../api/webOrder';

const unwrapRecords = response => {
  const data = response?.data || {};
  const rawRecords = data.payload ?? data.data ?? [];
  return camelcaseKeys(rawRecords, { deep: true });
};

export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  articleRecords: [],
  orderRecords: [],
  webCustomerRecords: [],
  webProductRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
    article: { isFetching: false },
    order: { isFetching: false },
    webCustomer: { isFetching: false },
    webProduct: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getArticleRecords(state) {
    return state.articleRecords;
  },
  getOrderRecords(state) {
    return state.orderRecords;
  },
  getWebCustomerRecords(state) {
    return state.webCustomerRecords;
  },
  getWebProductRecords(state) {
    return state.webProductRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, payload) {
    const { q, ...filters } = payload;
    if (!q && !Object.keys(filters).length) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q, ...filters }),
        dispatch('conversationSearch', { q, ...filters }),
        dispatch('messageSearch', { q, ...filters }),
        dispatch('articleSearch', { q, ...filters }),
        dispatch('orderSearch', { q, page: 1 }),
        dispatch('webCustomerSearch', { q, page: 1 }),
        dispatch('webProductSearch', { q, page: 1 }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ ...searchParams, page });
      commit(types.CONTACT_SEARCH_SET, data.payload.contacts);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async conversationSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({ ...searchParams, page });
      commit(types.CONVERSATION_SEARCH_SET, data.payload.conversations);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async messageSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ ...searchParams, page });
      commit(types.MESSAGE_SEARCH_SET, data.payload.messages);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async articleSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.articles({ ...searchParams, page });
      commit(types.ARTICLE_SEARCH_SET, data.payload.articles);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async orderSearch({ commit }, payload) {
    const { page = 1, q } = payload;
    if (!q) return;
    commit(types.ORDER_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const response = await webOrdersAPI.list({ q, page, per_page: 15 });
      commit(types.ORDER_SEARCH_SET, unwrapRecords(response));
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.ORDER_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async webCustomerSearch({ commit }, payload) {
    const { page = 1, q } = payload;
    if (!q) return;
    commit(types.WEB_CUSTOMER_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const response = await webCustomersAPI.list({ q, page, per_page: 15 });
      commit(types.WEB_CUSTOMER_SEARCH_SET, unwrapRecords(response));
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.WEB_CUSTOMER_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async webProductSearch({ commit }, payload) {
    const { page = 1, q } = payload;
    if (!q) return;
    commit(types.WEB_PRODUCT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const response = await webProductsAPI.list({ q, page, per_page: 15 });
      commit(types.WEB_PRODUCT_SEARCH_SET, unwrapRecords(response));
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.WEB_PRODUCT_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async clearSearchResults({ commit }) {
    commit(types.CLEAR_SEARCH_RESULTS);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = [...state.contactRecords, ...records];
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = [...state.conversationRecords, ...records];
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = [...state.messageRecords, ...records];
  },
  [types.ARTICLE_SEARCH_SET](state, records) {
    state.articleRecords = [...state.articleRecords, ...records];
  },
  [types.ORDER_SEARCH_SET](state, records) {
    state.orderRecords = [...state.orderRecords, ...records];
  },
  [types.WEB_CUSTOMER_SEARCH_SET](state, records) {
    state.webCustomerRecords = [...state.webCustomerRecords, ...records];
  },
  [types.WEB_PRODUCT_SEARCH_SET](state, records) {
    state.webProductRecords = [...state.webProductRecords, ...records];
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
  [types.ARTICLE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.article = { ...state.uiFlags.article, ...uiFlags };
  },
  [types.ORDER_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.order = { ...state.uiFlags.order, ...uiFlags };
  },
  [types.WEB_CUSTOMER_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.webCustomer = { ...state.uiFlags.webCustomer, ...uiFlags };
  },
  [types.WEB_PRODUCT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.webProduct = { ...state.uiFlags.webProduct, ...uiFlags };
  },
  [types.CLEAR_SEARCH_RESULTS](state) {
    state.contactRecords = [];
    state.conversationRecords = [];
    state.messageRecords = [];
    state.articleRecords = [];
    state.orderRecords = [];
    state.webCustomerRecords = [];
    state.webProductRecords = [];
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
